import { DOCUMENT } from '@angular/common';
import { Component, Inject } from '@angular/core';
import { Router } from '@angular/router';

import { Platform } from '@ionic/angular';

import { IUser } from '@shared/models/userTypes';

import { App, AppState, URLOpenListenerEvent } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import { SplashScreen } from '@capacitor/splash-screen';
import { environment } from '@environment';
import { Store } from '@ngxs/store';

import { SocialLogin } from '@capgo/capacitor-social-login';

import { NetworkService } from '@services/network.service';

import {
    AppversionService,
    CapgoUpdaterService,
    PresenceService,
    StatusBarService,
    ThemeService,
    UpdateAppService,
} from '@shared/services';
import { AvatarPersistenceService } from '@shared/services/avatar/avatar-persistence.service';
import { BackgroundTaskService } from '@shared/services/background-task.service';
import { CacheSharedApiService, FreshnessCheckService } from '@shared/services/cache';
import { DataOrchestratorService } from '@shared/services/data-orchestrator.service';
import { DeviceService } from '@shared/services/device.service';
import { FileSystemService } from '@shared/services/file-system.service';
import {
    FcmPushService,
    FcmService,
    FirebaseAnalyticsService,
    FirebaseCrashlyticsService,
} from '@shared/services/firebase';
import { ForegroundService } from '@shared/services/foreground.service';
import { OverlayService } from '@shared/services/overlay/overlay.service';
import { PwaUpdateService } from '@shared/services/pwa.service';
import { SocialLoginService } from '@shared/services/social-login/social-login.service';
import { StorageService } from '@shared/services/storage.service';
import { debounceTime, filter, pairwise } from 'rxjs/operators';
import { AuthService } from './core/services/auth.service';
import { RmsAuthService } from './core/services/rms-auth.service';
import { UserState } from './store/state/currentUser.state';

@Component({
    selector: 'app-root',
    templateUrl: 'app.component.html',
    styleUrls: ['app.component.scss'],
})
export class AppComponent {
    public user: firebase.default.User;
    public appversion;
    public buildVersion;
    currentUser: IUser | null;
    selectTheme: any;
    public themeColor = [
        { name: 'Default', class: 'default' },
        { name: 'corretor', class: 'corretor-theme' },
        { name: 'Purple', class: 'purple' },
        { name: 'Green', class: 'green' },
    ];
    currentTokens: any;
    constructor(
        private platform: Platform,
        private router: Router,
        private networkService: NetworkService,
        public overlayService: OverlayService,
        private themeService: ThemeService,
        private store: Store,
        @Inject(DOCUMENT) private document: Document,
        private firebaseAnalyticsService: FirebaseAnalyticsService,
        private firebaseCrashlyticsService: FirebaseCrashlyticsService,
        private statusBarService: StatusBarService,
        private appVersionService: AppversionService,
        private pwaUpdateService: PwaUpdateService,
        private updateAppService: UpdateAppService,
        private presenceService: PresenceService,
        private authService: AuthService,
        private rmsAuthService: RmsAuthService,
        private fileSystemService: FileSystemService,
        private dataOrchestrator: DataOrchestratorService,
        private deviceService: DeviceService,
        private cacheService: CacheSharedApiService,
        private freshnessCheckService: FreshnessCheckService,
        private foregroundService: ForegroundService,
        private backgroundService: BackgroundTaskService,
        private storageService: StorageService<any>,
        private socialLoginService: SocialLoginService,
        private capgoUpdaterService: CapgoUpdaterService,
        private avatarPersistence: AvatarPersistenceService,
        private fcmService: FcmService,
        private fcmPushService: FcmPushService,
    ) {
        this.initializeApp();
    }
    get loginOptionsGoogle() {
        return environment.loginOptionsGoogle;
    }

    async initializeApp() {
        /** Slides esconder e Status network */
        // pegando configuraçoes e guardando no localstorage
        this.platform.ready().then(async () => {
            this.setStatusBar();
            await this.storageService.init();

            // TASK: Fresh Start - Limpeza profunda uma única vez para garantir modo 100% remoto
            const FRESH_START_KEY = 'rms_fresh_start_v1_remote_v2'; // Incrementada versão para forçar nova limpeza
            const alreadyReset = await this.storageService.get(FRESH_START_KEY);

            if (!alreadyReset) {
                try {
                    // 1. Limpa TODOS os dados do Preferences (incluindo Auth, Cache Toggle, etc)
                    await this.storageService.clear();

                    // 2. Limpa LocalStorage (Service Worker, caches legados)
                    localStorage.clear();

                    // 3. Limpa arquivos locais (Imagens, PDFs)
                    await this.fileSystemService.clearAllFiles();

                    // 4. Garante que o cache inicie DESATIVADO no novo estado
                    // (O StorageService.clear() já removeu a chave, então isCacheEnabled() retornará false)

                    // 5. Marca como limpo para não repetir no próximo boot
                    // Precisamos re-inicializar o storage pois o clear() pode ter afetado o estado interno
                    await this.storageService.init();
                    await this.storageService.set(FRESH_START_KEY, true);

                    // Opcional: Recarregar o app para garantir estado limpo em todos os serviços
                    // window.location.reload();
                } catch (error) {
                    console.error('❌ [FreshStart] Erro durante a limpeza total:', error);
                }
            }

            await SplashScreen.hide();
            this.socialInitialize();

            // IMPORTANTE: Aguarda Firebase inicializar antes de usar outros serviços
            await this.initFirabase();
            this.fcmPushService.initialize();

            // Garante RMS JWT para sessões já ativas (sem forçar re-login)
            this.rmsAuthService.ensureRmsJwt();

            this.presenceService.updateOnUser().subscribe();
            this.setVersionBuild();
            this.initializeNetworkEvents();
            this.checkLoggedIn();
            this.loadCurrentUser();
            this.initDeepLinks();
            this.checkIsUpdatePwa();
            this.setupAppResumeHandler();
            this.onAppResume(); // verifica freshness no cold start também

            // Verificação de manutenção + update nativo (Firestore configuracao/manutencao + Play/App Store)
            this.updateAppService.checkUpdate().subscribe();

            // OTA updates via Capgo
            this.capgoUpdaterService.init();
        });
    }

    async setStatusBar() {
        this.statusBarService.setStatusBar();
    }

    dynamicTheme() {
        this.themeService.activeTheme(this.selectTheme);
    }

    private initializeNetworkEvents() {
        this.networkService.initializeNetworkEvents();
    }

    async logout(): Promise<void> {
        this.presenceService.signOut();
        // Unsubscribe from broker leads FCM topic
        if (this.currentUser?.uid) {
            await this.fcmService.unsubscribeFromBrokerLeadsTopic(this.currentUser.uid);
        }
        await this.fcmPushService.unregisterToken();
        this.cleanBeforeLogout();
        await this.authService.logout();
        this.setStatusBar();
    }

    loadCurrentUser() {
        this.store
            .select(UserState.getCurrentUser)
            .pipe()
            .subscribe((user) => {
                this.currentUser = user;
                if (user) {
                    this.fcmPushService.login(user.uid);
                    this.fcmPushService.requestToken();
                    // Subscribe to broker leads FCM topic if user is an active broker
                    if (user?.corretor === true && user?.ativo !== false && user?.uid) {
                        this.fcmService.subscribeToBrokerLeadsTopic(user.uid);
                    }
                }
                if (user && user?.isAdmin) {
                    this.selectTheme = 'admin-theme';
                } else if (user && user?.corretor) {
                    this.selectTheme = 'corretor-theme';
                } else {
                    this.selectTheme = 'default';
                }
                this.dynamicTheme();
            });
    }

    private async initFirabase() {
        if (Capacitor.getPlatform() === 'web') {
            return;
        }

        try {
            // Aguarda inicialização dos serviços Firebase
            await this.firebaseAnalyticsService.enable();
            this.firebaseAnalyticsService.setCurrentScreen('appComponent::inicio');
            await this.firebaseCrashlyticsService.enable();

            const userId = this.user?.uid ?? this.currentUser?.uid;
            if (userId) {
                await this.firebaseAnalyticsService.setUserId();
                await this.firebaseCrashlyticsService.setUserId(userId);
            }
        } catch (error) {
            // Não bloqueia o app em caso de erro
        }
    }
    private async checkLoggedIn() {
        if (!this.networkService.isOnlineSignal()) return; // Ignora se estiver offline no boot

        await SocialLogin.refresh({
            provider: 'google',
            options: this.loginOptionsGoogle,
        })
            .then((data) => {})
            .catch((error) => {});
    }
    private async signin() {
        if (!this.networkService.isOnlineSignal()) return;

        try {
            await SocialLogin.login({
                provider: 'google',
                options: this.loginOptionsGoogle,
            });
        } catch (error) {
            console.error('Erro ao fazer login:', error);
            // Só alerta se não for erro de cancelamento e estiver online
            if (!String(error).includes('cancel')) {
                this.overlayService.alert({
                    message: 'Não foi possível autenticar. Verifique sua conexão.',
                    buttons: [{ text: 'OK' }],
                });
            }
        }
    }
    private async setVersionBuild() {
        try {
            const versionNumberPromise = this.appVersionService.getVersionNumber();
            const buildVersionPromise = this.appVersionService.getBuildVersion();
            const [versionNumber, buildVersion] = await Promise.all([
                versionNumberPromise,
                buildVersionPromise,
            ]);
            this.appversion = versionNumber;
            this.buildVersion = buildVersion;
        } catch (error) {
            this.appversion = environment.configApp.versionApp;
            this.buildVersion = environment.configApp.buildVersion;
        }
    }
    private checkIsUpdatePwa() {
        if (Capacitor.getPlatform() === 'web') {
            this.pwaUpdateService.updateActivated;
        }
    }

    /**
     * Inicializa o SocialLogin de forma centralizada
     * Usa o SocialLoginService para evitar múltiplas inicializações
     */
    async socialInitialize() {
        await this.socialLoginService.initialize();
    }

    /**
     * Configura detecção de resume do app para verificar dados stale.
     * Também observa restauração de rede para retry quando estava offline.
     */
    private setupAppResumeHandler(): void {
        let resumeTimeout: ReturnType<typeof setTimeout>;

        App.addListener('appStateChange', (state: AppState) => {
            if (!state.isActive) return;
            clearTimeout(resumeTimeout);
            resumeTimeout = setTimeout(() => this.onAppResume(), 2000);
        });

        // TASK 6: quando rede volta (offline → online), refaz a verificação
        this.networkService.isOnline$
            .pipe(
                pairwise(),
                filter(([prev, curr]) => !prev.connected && curr.connected),
                debounceTime(3000),
            )
            .subscribe(() => this.onAppResume());
    }

    /**
     * Executado ao retornar ao foreground ou quando a rede é restaurada.
     * Reestabelece o canal Supabase e invalida cache se houver dados novos no servidor.
     */
    private onAppResume(): void {
        this.freshnessCheckService.checkFreshness().subscribe((isStale) => {
            if (!isStale) return;
            this.cacheService.clearCachedData().then(() => {
                this.freshnessCheckService.updateLastSyncTimestamp();
            });
        });

        // Retry de uploads de avatar pendentes
        this.avatarPersistence.processarFila();

        // Single active session (anti-fraude): verifica se outro dispositivo assumiu a sessão
        this.checkActiveSession();
    }

    /**
     * Single active session — verifica no Auth Service se esta sessão ainda é a ativa.
     * Se outro dispositivo logou e derrubou esta sessão, faz logout completo (Firebase
     * inclusive) para evitar que o re-exchange silencioso reassuma a sessão (steal-back).
     *
     * Fail-open: validateActiveSession nunca derruba por indisponibilidade do Auth Service.
     */
    private async checkActiveSession(): Promise<void> {
        // Só faz sentido com um JWT válido localmente — sessão superada mantém o token
        // íntegro (só deixou de ser a ativa). Sem token válido, os fluxos de auth tratam.
        if (!this.rmsAuthService.isStoredTokenValid()) return;

        const { active } = await this.rmsAuthService.validateActiveSession();
        if (active) return;

        await this.overlayService.toast({
            message: 'Sua conta foi acessada em outro dispositivo. Faça login novamente.',
            duration: 4000,
            position: 'top',
            color: 'danger',
        });
        await this.authService.logout();
    }

    /**
     * Registra listener de deep links.
     * Suporta:
     *   https://api.planosdesauderms.com.br/wp-json/custom/v3/planos/2746  (App Links / Universal Links)
     *   rmscorretora://tabelas-details/2151                                (Custom URL Scheme — fallback)
     */
    private initDeepLinks(): void {
        App.addListener('appUrlOpen', (event: URLOpenListenerEvent) => {
            this.handleDeepLink(event.url);
        });
    }

    private handleDeepLink(url: string): void {
        try {
            const parsed = new URL(url);
            let routePath: string | null = null;

            if (parsed.protocol === 'https:' || parsed.protocol === 'http:') {
                // API URL: /wp-json/custom/v3/planos/2746 → /tabelas-details/2746
                const planoMatch = parsed.pathname.match(/\/wp-json\/custom\/v\d+\/planos\/(\d+)/);
                if (planoMatch) {
                    routePath = `/tabelas-details/${planoMatch[1]}`;
                } else {
                    routePath = parsed.pathname;
                }
            } else {
                // rmscorretora://tabelas-details/2151 → /tabelas-details/2151
                routePath = `/${parsed.host}${parsed.pathname}`;
            }

            if (routePath && routePath !== '/') {
                this.router.navigateByUrl(routePath);
            }
        } catch (e) {}
    }

    private cleanBeforeLogout() {
        this.cacheService.clearCachedData().then(() => {});
        this.deviceService.stopBatteryMonitoring();
        this.foregroundService.stop();
        this.backgroundService.stop();
        this.fileSystemService.clearAllFiles();
        this.dataOrchestrator.taskStatus$?.unsubscribe();
    }
}
